import React, { useState } from 'react';

const CsvExport = ({ looks, season }) => {
  const [validation, setValidation] = useState(null);
  const [showPanel, setShowPanel] = useState(false);

  const seasonLooks = (looks || []).filter(l => l.season === season);

  // A collage is "look-only" if all its photos reference existing looks or database poses
  // (no direct student photo placement needed, so face/eye data is optional)
  const isLookOnlyCollage = (look) => {
    if (look.look_type !== 'collage') return false;
    const photos = look.collage_photos || [];
    if (photos.length === 0) return false;
    return photos.every(p => p.look_ref || (p.source_type === 'database' && p.pose_id));
  };

  const validate = () => {
    const errors = [];
    const warnings = [];

    if (seasonLooks.length === 0) {
      errors.push(`No looks found for season "${season || '(none)'}".`);
      return { errors, warnings };
    }

    seasonLooks.forEach(look => {
      const prefix = `Look "${look.name || '(unnamed)'}"`;

      if (!look.name) errors.push(`${prefix}: Missing name`);
      if (!look.canvas_width || !look.canvas_height) errors.push(`${prefix}: Missing canvas dimensions`);
      if (look.look_type === 'single' && !look.source_if_3 && !look.source_if_2 && !look.source_if_1) {
        errors.push(`${prefix}: Single look has no pose source (source_if_3, source_if_2, or source_if_1)`);
      }

      if (!isLookOnlyCollage(look)) {
        if (look.eyes_y == null && look.face_height == null && look.center_x == null) {
          if (look.look_type === 'single') {
            errors.push(`${prefix}: Missing face placement data (eyes_y, face_height, center_x)`);
          }
        }
      }

      if (look.look_type === 'collage' && !look.canvas_filepath) {
        warnings.push(`${prefix}: Collage has no canvas background S3 path`);
      }
      if (look.look_type === 'collage' && !look.canvas_filename) {
        warnings.push(`${prefix}: Collage has no canvas background filename`);
      }
      if (look.overlay_s3_path && !look.overlay_s3_path.startsWith('s3://') && !look.overlay_s3_path.startsWith('favorites_designer/')) {
        warnings.push(`${prefix}: Overlay S3 path looks malformed`);
      }
      if (look.look_type === 'collage' && (!look.layer_order || look.layer_order.length === 0)) {
        warnings.push(`${prefix}: No layer order defined — default order will be used`);
      }

      // Validate collage photos have required data
      if (look.look_type === 'collage') {
        (look.collage_photos || []).forEach((photo, pi) => {
          const pp = `${prefix} Photo ${pi + 1}`;
          if (!photo.look_ref && !photo.pose_id) {
            warnings.push(`${pp}: No look or pose selected`);
          }
          if (photo.x == null || photo.y == null) {
            warnings.push(`${pp}: Missing position (x/y)`);
          }
          if (photo.width == null || photo.height == null) {
            warnings.push(`${pp}: Missing dimensions (width/height)`);
          }
          if (photo.source_type === 'database' && !photo.pose_s3_key) {
            warnings.push(`${pp}: Database pose missing S3 key`);
          }
        });
      }

      (look.text_fields || []).forEach((tf, i) => {
        const fPrefix = `${prefix} Field ${i + 1}`;
        if (tf.bound_x_left != null && tf.bound_x_right != null && look.canvas_width && tf.font_size_max) {
          const boundWidthPx = (tf.bound_x_right - tf.bound_x_left) * look.canvas_width;
          if (boundWidthPx < tf.font_size_max * 3) {
            warnings.push(`${fPrefix}: Bounding box may be too narrow for font size`);
          }
        }
      });
    });

    return { errors, warnings };
  };

  const handleGenerate = () => {
    const result = validate();
    setValidation(result);
    setShowPanel(true);
  };

  // Helper: convert percentage to pixel value
  const pctToPx = (pct, dimension) => {
    if (pct == null || dimension == null) return '';
    return Math.round(pct * dimension);
  };

  const generateCsv = () => {
    if (seasonLooks.length === 0) return;

    const maxFields = Math.max(0, ...seasonLooks.map(l => (l.text_fields || []).length));
    const maxPhotos = Math.max(0, ...seasonLooks.map(l => (l.collage_photos || []).length));
    const maxLayers = Math.max(0, ...seasonLooks.map(l => (l.layer_order || []).length));

    // === BUILD HEADER ===
    const headers = [
      // Look identity
      'id', 'season', 'pose', 'type', 'look_only_collage',
      // Source pose selection (singles)
      'source_if_3', 'source_if_2', 'source_if_1',
      // Canvas dimensions
      'canvas_width', 'canvas_height', 'canvas_dpi',
      // Collage background S3 reference
      'canvas_bg_filepath', 'canvas_bg_filename',
      // Face placement (percentages 0.0–1.0)
      'face_eyes_y', 'face_height', 'face_center_x', 'face_rotation',
      // Face placement (pixel values)
      'face_eyes_y_px', 'face_height_px', 'face_center_x_px',
      // Designer guide lines (percentages)
      'guide_eye_y', 'guide_head_y', 'guide_face_top_y', 'guide_face_bottom_y',
      // Element counts
      'collage_photo_count', 'text_field_count', 'layer_count',
    ];

    // Collage photo columns (dynamic)
    for (let n = 1; n <= Math.max(2, maxPhotos); n++) {
      headers.push(
        `photo_${n}_source_type`,
        `photo_${n}_id`,
        `photo_${n}_pose_id`,
        `photo_${n}_season`,
        `photo_${n}_pose_s3_key`,
        `photo_${n}_x`, `photo_${n}_y`,
        `photo_${n}_x_px`, `photo_${n}_y_px`,
        `photo_${n}_width`, `photo_${n}_height`,
        `photo_${n}_width_px`, `photo_${n}_height_px`
      );
    }

    // Overlay
    headers.push('overlay_path', 'overlay_filename');

    // Mascot
    headers.push('mascot_center_x', 'mascot_center_y', 'mascot_size');

    // Layer order (dynamic)
    for (let n = 1; n <= maxLayers; n++) {
      headers.push(`layer_${n}_type`, `layer_${n}_index`);
    }

    // Text fields (dynamic)
    for (let n = 1; n <= maxFields; n++) {
      headers.push(
        `field_${n}_position`, `field_${n}_content`,
        `field_${n}_font_filepath`, `field_${n}_font_filename`, `field_${n}_font_color`,
        `field_${n}_font_size_min_pt`, `field_${n}_font_size_min_pct`,
        `field_${n}_font_size_max_pt`, `field_${n}_font_size_max_pct`,
        `field_${n}_center_x`, `field_${n}_center_y`,
        `field_${n}_center_x_px`, `field_${n}_center_y_px`,
        `field_${n}_bound_x_left`, `field_${n}_bound_x_right`,
        `field_${n}_bound_x_left_px`, `field_${n}_bound_x_right_px`,
        `field_${n}_sizing_rule`, `field_${n}_align`,
        `field_${n}_bold`, `field_${n}_italic`, `field_${n}_shadow`
      );
    }

    // === BUILD ROWS ===
    const rows = seasonLooks.map(look => {
      const cw = look.canvas_width || 0;
      const ch = look.canvas_height || 1;
      const lookOnly = isLookOnlyCollage(look);
      const photos = look.collage_photos || [];
      const fields = look.text_fields || [];
      const layers = look.layer_order || [];

      const row = [
        // Look identity
        look.name || '',
        look.season || '',
        look.pose_number || '',
        look.look_type || '',
        lookOnly ? 'true' : 'false',
        // Source pose selection
        look.source_if_3 || '',
        look.source_if_2 || '',
        look.source_if_1 || '',
        // Canvas dimensions
        cw ? `${cw}px` : '',
        ch ? `${ch}px` : '',
        look.canvas_dpi ?? '',
        // Collage background
        look.canvas_filepath || '',
        look.canvas_filename || '',
        // Face placement (percentages)
        look.eyes_y ?? '',
        look.face_height ?? '',
        look.center_x ?? '',
        look.rotation ?? '',
        // Face placement (pixels)
        pctToPx(look.eyes_y, ch),
        pctToPx(look.face_height, ch),
        pctToPx(look.center_x, cw),
        // Guide lines
        look.guide_eye_y ?? '',
        look.guide_head_y ?? '',
        look.guide_face_top_y ?? '',
        look.guide_face_bottom_y ?? '',
        // Element counts
        photos.length,
        fields.length,
        layers.length,
      ];

      // Collage photos
      for (let n = 0; n < Math.max(2, maxPhotos); n++) {
        const photo = photos[n];
        if (photo) {
          const isDb = photo.source_type === 'database';
          row.push(
            photo.source_type || 'look',
            isDb ? (photo.pose_name || '') : (photo.look_ref || ''),
            isDb ? (photo.pose_id || '') : '',
            photo.season || '',
            photo.pose_s3_key || '',
            photo.x ?? '',
            photo.y ?? '',
            pctToPx(photo.x, cw),
            pctToPx(photo.y, ch),
            photo.width ?? '',
            photo.height ?? '',
            pctToPx(photo.width, cw),
            pctToPx(photo.height, ch)
          );
        } else {
          row.push('', '', '', '', '', '', '', '', '', '', '', '', '');
        }
      }

      // Overlay
      row.push(look.overlay_s3_path || '', look.overlay_filename || '');

      // Mascot
      row.push(look.mascot_center_x ?? '', look.mascot_center_y ?? '', look.mascot_size ?? '');

      // Layer order
      for (let n = 0; n < maxLayers; n++) {
        const layer = layers[n];
        if (layer) {
          row.push(layer.type || '', layer.index != null ? String(layer.index) : '');
        } else {
          row.push('', '');
        }
      }

      // Text fields
      for (let n = 0; n < maxFields; n++) {
        const tf = fields[n];
        if (tf) {
          row.push(
            tf.position ?? n,
            tf.content || '',
            tf.font_s3_path || '',
            tf.font_filename || '',
            tf.font_color || '',
            tf.font_size_min != null ? `${tf.font_size_min}pt` : '',
            tf.font_size_min != null ? (tf.font_size_min / ch).toFixed(6) : '',
            tf.font_size_max != null ? `${tf.font_size_max}pt` : '',
            tf.font_size_max != null ? (tf.font_size_max / ch).toFixed(6) : '',
            tf.center_x ?? '',
            tf.center_y ?? '',
            pctToPx(tf.center_x, cw),
            pctToPx(tf.center_y, ch),
            tf.bound_x_left ?? '',
            tf.bound_x_right ?? '',
            pctToPx(tf.bound_x_left, cw),
            pctToPx(tf.bound_x_right, cw),
            tf.sizing_rule || '',
            tf.align || '',
            tf.bold ? 'true' : 'false',
            tf.italic ? 'true' : 'false',
            tf.shadow ? 'true' : 'false'
          );
        } else {
          // 22 empty columns per text field
          row.push('', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '');
        }
      }

      return row;
    });

    // === GENERATE CSV STRING ===
    const escapeCsv = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map(r => r.map(escapeCsv).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${season || 'favorites'}_favorites_spec.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasErrors = validation?.errors?.length > 0;
  const hasWarnings = validation?.warnings?.length > 0;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleGenerate}
        style={{
          padding: '8px 16px', background: '#fff', border: '1px solid #ccc',
          borderRadius: 4, cursor: 'pointer', fontSize: 13
        }}
      >
        Generate CSV
      </button>

      {showPanel && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: '#fff', border: '1px solid #ccc', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,.15)', padding: 16,
          zIndex: 200, width: 420, maxHeight: 400, overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>CSV Export Validation</span>
            <button
              onClick={() => setShowPanel(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#999' }}
            >
              &times;
            </button>
          </div>

          {hasErrors && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#d32f2f', marginBottom: 4 }}>
                Errors ({validation.errors.length})
              </div>
              {validation.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 11, color: '#d32f2f', padding: '2px 0', paddingLeft: 8 }}>
                  {e}
                </div>
              ))}
            </div>
          )}

          {hasWarnings && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e65100', marginBottom: 4 }}>
                Warnings ({validation.warnings.length})
              </div>
              {validation.warnings.map((w, i) => (
                <div key={i} style={{ fontSize: 11, color: '#e65100', padding: '2px 0', paddingLeft: 8 }}>
                  {w}
                </div>
              ))}
            </div>
          )}

          {!hasErrors && !hasWarnings && (
            <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 12 }}>
              All looks pass validation.
            </div>
          )}

          <button
            onClick={generateCsv}
            disabled={hasErrors}
            style={{
              width: '100%', padding: '8px 16px',
              background: hasErrors ? '#ccc' : '#1976d2',
              color: '#fff', border: 'none', borderRadius: 4,
              cursor: hasErrors ? 'default' : 'pointer',
              fontSize: 13, fontWeight: 500
            }}
          >
            {hasErrors
              ? 'Fix errors to download'
              : hasWarnings
                ? `Download CSV (${validation.warnings.length} warnings)`
                : 'Download CSV'}
          </button>

          <div style={{ fontSize: 10, color: '#999', marginTop: 8, textAlign: 'center' }}>
            {seasonLooks.length} look{seasonLooks.length !== 1 ? 's' : ''} for {season}
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvExport;
